import {
  CoverageCalculator,
  CoverageCalculatorOptions,
  CustomerInfo,
  ItemInfo,
} from ".";
import { PricingRuleSet } from "../team";
import {
  determineRuleSet,
  getApplicableRule,
  getPricingRulesInLocalCurrency,
  listsHaveMatchingElement,
} from "./calculator-utils";
import { itemValidForProtection } from "./check-product";

const isProductEnabled = (team: CoverageCalculatorOptions) => {
  return !!(
    team.settings.packageProtection?.enabled &&
    team.settings.packageProtection?.coverage
  );
};

const getShopperEligibilityInfo = (
  customerInfo: CustomerInfo,
  team: CoverageCalculatorOptions,
) => {
  if (
    listsHaveMatchingElement(
      customerInfo.customerTags,
      team.settings.coverageExcludeCustomerTags,
    )
  ) {
    return { isEligible: false, reasonIneligible: "customer_tags" };
  }

  const packageEligibleCountries =
    getAllPackageProtectionEligibleCountries(team);

  const ValidCountry =
    packageEligibleCountries.includes(customerInfo.country) ||
    packageEligibleCountries.length === 0;
  if (!ValidCountry) {
    return {
      isEligible: false,
      reasonIneligible: "shopper is in an ineligible country",
    };
  }

  if (
    customerInfo.isBusinessAccount &&
    team.settings.shopifyExtension?.disableBusinessAccountInStorefront
  ) {
    return { isEligible: false, reasonIneligible: "business account excluded" };
  }

  if (customerInfo.usingZapietPickup) {
    return { isEligible: false, reasonIneligible: "zapiet pickup excluded" };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

const getItemEligibilityInfo = (
  item: ItemInfo,
  team: CoverageCalculatorOptions,
) => {
  if (item.vendor === "re:do") {
    return { isEligible: false, reasonIneligible: "redo product excluded" };
  }

  if (item.requiresShipping === false) {
    return {
      isEligible: false,
      reasonIneligible: "item does not require shipping",
    };
  }

  const isItemValid = itemValidForProtection({
    itemInfo: {
      productTags: item.productTags,
      collections: item.collections,
      properties: item.properties,
    },
    invalidProductTags: team.settings.protectionExcludeTags ?? [],
  });

  if (!isItemValid) {
    return { isEligible: false, reasonIneligible: "excluded product tags" };
  }

  return { isEligible: true, reasonIneligible: undefined };
};

// returns a list of countries. Empty list means all countries
function getAllPackageProtectionEligibleCountries(
  team: CoverageCalculatorOptions,
) {
  // Match autocheck logic exactly:
  // If pricingRuleSet[1] exists (international pricing), return [] (all countries eligible)
  // Otherwise, use pricingRuleSet[0].countries, falling back to dedicated countries field
  if (team.settings.packageProtection?.pricingRuleSet?.[1]) {
    return [];
  }

  const countriesFromPricing =
    team.settings.packageProtection?.pricingRuleSet?.[0]?.countries;
  const countriesFromDedicatedField =
    team.settings?.packageProtection?.countries;

  return countriesFromPricing ?? countriesFromDedicatedField ?? [];
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function getPackageProtectionPrice(
  cartTotal: number,
  pricingRuleSets: PricingRuleSet[] | undefined,
  country: string | undefined | null,
  minPrice: number,
  maxPrice: number,
  backupPercentage: number,
  fixedSubscriptionPrice?: number,
  cartHasSubscriptionItem?: boolean,
) {
  if (
    country === undefined ||
    !maxPrice ||
    (!backupPercentage && !pricingRuleSets?.length)
  ) {
    return 0;
  }

  if (cartHasSubscriptionItem && fixedSubscriptionPrice) {
    return parseFloat(fixedSubscriptionPrice.toFixed(2));
  }

  if (!pricingRuleSets?.length) {
    return clamp(cartTotal * backupPercentage, minPrice, maxPrice);
  }

  const ruleset = determineRuleSet(pricingRuleSets, country);
  if (ruleset) {
    const rule = getApplicableRule(ruleset, cartTotal);
    const price =
      ruleset.type === "percentage" ? rule.value * cartTotal : rule.value;
    return clamp(price, minPrice, maxPrice);
  }

  return parseFloat(
    clamp(cartTotal * backupPercentage, minPrice, maxPrice).toFixed(2),
  );
}

const calculateCoveragePrice = (
  team: CoverageCalculatorOptions,
  country: string | undefined,
  numProducts: number,
  amount: number,
  exchangeRate?: number,
  hasSubscriptionItem?: boolean,
): number => {
  if (exchangeRate) {
    if (team.settings.packageProtection?.minPrice) {
      team.settings.packageProtection.minPrice *= exchangeRate;
    }
    if (team.settings.packageProtection?.maxPrice) {
      team.settings.packageProtection.maxPrice *= exchangeRate;
    }

    if (team.settings.packageProtection?.pricingRuleSet) {
      team.settings.packageProtection.pricingRuleSet =
        getPricingRulesInLocalCurrency(
          team.settings.packageProtection.pricingRuleSet,
          exchangeRate,
        );
    }
  }

  let coveragePrice = 0;
  if (team.settings.packageProtection) {
    const ruleSets = team.settings.packageProtection?.pricingRuleSet;
    const minPrice = team.settings.packageProtection.minPrice;
    const maxPrice = team.settings.packageProtection.maxPrice;
    const backupPercentage = team.settings.packageProtection.percentage;
    const fixedSubscriptionPrice =
      team.settings.packageProtection.fixedSubscriptionPrice ?? undefined;

    coveragePrice = getPackageProtectionPrice(
      amount,
      ruleSets,
      country,
      minPrice,
      maxPrice,
      backupPercentage,
      fixedSubscriptionPrice,
      hasSubscriptionItem,
    );
  }

  return coveragePrice;
};

// returns a price if coverage is valid. otherwise returns undefined
const getCoverage = (
  team: CoverageCalculatorOptions,
  customerInfo: CustomerInfo,
  items: ItemInfo[],
  conversionRate?: number,
  ignoreMerchantPaidForEnablementCheck?: boolean,
): { eligible: boolean; price?: number; reasonIneligible?: string } => {
  if (!isProductEnabled(team)) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible: "product not enabled",
    };
  }

  const shopperEligibleInfo = getShopperEligibilityInfo(customerInfo, team);
  if (!shopperEligibleInfo.isEligible) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible: shopperEligibleInfo.reasonIneligible,
    };
  }

  let totalPrice = 0;
  let totalValidPrice = 0;
  let numValidItems = 0;
  let numInvalidItems = 0;
  const validItems = [];

  const ineligibleItemReasons = [];

  for (const item of items) {
    const itemPrice = item.price * item.quantity;
    totalPrice += itemPrice;
    const itemEligibleInfo = getItemEligibilityInfo(item, team);
    if (itemEligibleInfo.isEligible) {
      totalValidPrice += itemPrice;
      numValidItems += item.quantity;
      validItems.push(item);
    } else {
      numInvalidItems += item.quantity;
      ineligibleItemReasons.push(itemEligibleInfo.reasonIneligible);
    }
  }

  const cartEligibleInfo = getCartEligibilityInfo(
    numValidItems,
    numInvalidItems,
    totalPrice,
    team,
  );
  if (!cartEligibleInfo.isEligible) {
    return {
      eligible: false,
      price: undefined,
      reasonIneligible:
        cartEligibleInfo.reasonIneligible || ineligibleItemReasons.join(", "),
    };
  }

  const price = calculateCoveragePrice(
    team,
    customerInfo.country,
    numValidItems,
    totalValidPrice,
    conversionRate,
    validItems.some((item) => item.hasSubscription),
  );

  return { eligible: true, price, reasonIneligible: undefined };
};

const getCartEligibilityInfo = (
  numValidItems: number,
  numInvalidItems: number,
  cartTotal: number,
  team: CoverageCalculatorOptions,
) => {
  if (
    team.settings.packageProtection?.minCoverageEligibleCartTotal &&
    cartTotal < team.settings.packageProtection?.minCoverageEligibleCartTotal
  ) {
    return { isEligible: false, reasonIneligible: "cart total below minimum" };
  }
  if (
    team.settings.packageProtection?.maxCoverageEligibleCartTotal &&
    cartTotal > team.settings.packageProtection?.maxCoverageEligibleCartTotal
  ) {
    return { isEligible: false, reasonIneligible: "cart total above maximum" };
  }

  if (team.settings.excludeCoverageIfAnyInvalidProduct && numInvalidItems > 0) {
    return {
      isEligible: false,
      reasonIneligible: "cart has at least one invalid product",
    };
  }

  return { isEligible: numValidItems > 0, reasonIneligible: undefined };
};

export const packageProtectionCoverageCalculator: CoverageCalculator = {
  type: "Package Protection",
  isProductEnabled,
  getShopperEligibilityInfo,
  getItemEligibilityInfo,
  getCartEligibilityInfo,
  // need to handle non usd here.
  calculateCoveragePrice,
  getCoverage,
};
